import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

const IS_PERSIST_TOKEN = String(process.env.REACT_APP_AUTH_PERSIST_SESSION ?? 'true').trim().toLowerCase() === 'true';
const STORAGE_KEY = 'LStudio__app_session';

const AppAuthContext = createContext(null);

export const useAuth = () => {

    const CONTEXT = useContext(AppAuthContext);

    if (!CONTEXT) {
        throw new Error('useAuth() se usó fuera de <AppAuthProvider>. Revisá App.js');
    }

    return CONTEXT;
};

export const AppAuthProvider = ({ children, onLoginRequest = null, onRestoreRequest = null, onLogoutRequest = null }) => {

    const [useUser, setUser] = useState(null);
    const [useToken, setToken] = useState(null);
    const [useIsLoading, setIsLoading] = useState(true);

    useEffect(() => {

        let isActive = true;

        const onRestore = async () => {
            try {
                if (IS_PERSIST_TOKEN) {
                    const RAW = window.localStorage.getItem(STORAGE_KEY);
                    if (RAW) {
                        const SESSION = JSON.parse(RAW);
                        if (isActive) { setUser(SESSION.user); setToken(SESSION.token); }
                    }
                }
                if (onRestoreRequest) {
                    const USER = await onRestoreRequest();
                    if (isActive && USER) setUser(USER);
                }
            } catch {
            } finally {
                if (isActive) setIsLoading(false);
            }
        };
        onRestore();
        return () => { isActive = false; };

    }, [onRestoreRequest]);

    const CB__onLogin = useCallback(async (credentials) => {

        const RESULT = await onLoginRequest?.(credentials);

        if (!RESULT?.user) throw new Error('No se pudo iniciar sesión');

        setUser(RESULT.user);
        setToken(RESULT.token ?? null);

        if (IS_PERSIST_TOKEN) {
            try {
                window.localStorage.setItem(STORAGE_KEY, JSON.stringify(RESULT));
            } catch { }
        }

        return RESULT.user;

    }, [onLoginRequest]);

    const CB__onLogout = useCallback(async () => {

        try { await onLogoutRequest?.(); } catch { /* igual cerramos local */ }

        setUser(null);
        setToken(null);

        try { window.localStorage.removeItem(STORAGE_KEY); } catch { /* nada */ }

    }, [onLogoutRequest]);

    const CB__onHasRole = useCallback((roles) => {

        if (!roles || roles.length === 0) return true;

        if (!useUser) return false;

        const LIST = Array.isArray(roles) ? roles : [roles];

        return LIST.includes(useUser.role);

    }, [useUser]);

    const CB__onHasPermission = useCallback((permissions) => {

        if (!permissions || permissions.length === 0) return true;

        if (!useUser) return false;

        const LIST = Array.isArray(permissions) ? permissions : [permissions];

        const OWNED = useUser.permissions ?? [];

        return LIST.some((p) => OWNED.includes(p));

    }, [useUser]);

    const CB__onHasEveryPermission = useCallback((permissions) => {

        if (!permissions || permissions.length === 0) return true;

        if (!useUser) return false;

        const OWNED = useUser.permissions ?? [];

        return permissions.every((p) => OWNED.includes(p));

    }, [useUser]);

    const MEMO__VALUE = useMemo(() => ({
        user: useUser, token: useToken, isAuth: !!useUser,
        isLoading: useIsLoading, login: CB__onLogin, logout: CB__onLogout,
        setUser, hasRole: CB__onHasRole, hasPermission: CB__onHasPermission,
        hasEveryPermission: CB__onHasEveryPermission,
    }), [
        useUser, useToken, useIsLoading, CB__onLogin, CB__onLogout, CB__onHasRole,
        CB__onHasPermission, CB__onHasEveryPermission,
    ]);

    return <AppAuthContext.Provider value={MEMO__VALUE}>{children}</AppAuthContext.Provider>;
};
